/**
 * Share-key VIEWER path (P3.md "Viewer mode"; fixes the 2026-07-02 review
 * finding "share-key viewer URL parameter not parsed").
 *
 * A crew member opens a printed link — either the v1 form `/?person=<key>` or
 * the direct `/s/<key>` (302'd by the server to the former). This module is the
 * ONLY thing that turns that opaque key into on-screen data:
 *
 *  1. `shareKeyFromUrl()` — pull the key out of the address bar (query or path).
 *  2. `fetchViewerGigs()` — GET /api/viewer/gigs with the key in the
 *     `X-Share-Key` HEADER (never `?key=` in the URL — ADR-014: the key is a
 *     credential and must not land in server request logs). The server scopes
 *     every gig through the ONE PII module before it leaves.
 *  3. `scrubShareKeyFromUrl()` — replace the URL with `/` so the credential
 *     never lingers in the address bar / history (ADR-014).
 *
 * Viewers get NO outbox, NO SSE, NO authenticated /changes (App.tsx branches on
 * this): the returned gigs are already read-only and pre-scoped.
 */
import type { GigRecord } from "../db/idb.ts";

/** The scoped wire shape /api/viewer/gigs returns (packages/scoping ScopedGig). */
interface ScopedWireGig {
  gig_id: string;
  fields?: Record<string, unknown>;
  collections?: Record<string, Array<Record<string, unknown>>>;
}

/** Minimum plausible key length — mirrors the server's `viewer()` guard (share.ts). */
const MIN_KEY_LEN = 8;

/**
 * Extract the share key from the address bar: `?person=` (v1 printed form) or
 * `?key=`, else the `/s/<key>` path. Returns null for a normal owner/editor
 * session so the caller falls through to the authenticated boot.
 */
export function shareKeyFromUrl(): string | null {
  try {
    if (typeof location === "undefined") return null;
    const q = new URLSearchParams(location.search);
    const fromQuery = q.get("person") ?? q.get("key");
    if (fromQuery !== null && fromQuery.length >= MIN_KEY_LEN) return fromQuery;
    const captured = /^\/s\/([^/?#]+)/.exec(location.pathname)?.[1];
    if (captured !== undefined) {
      const k = decodeURIComponent(captured);
      if (k.length >= MIN_KEY_LEN) return k;
    }
  } catch {
    /* non-browser (tests) */
  }
  return null;
}

/** Drop the key from the address bar + history entry (ADR-014). */
export function scrubShareKeyFromUrl(): void {
  try {
    if (typeof history !== "undefined" && typeof history.replaceState === "function") {
      history.replaceState(null, "", "/");
    }
  } catch {
    /* non-browser / disabled history */
  }
}

/**
 * Convert one already-scoped wire gig into the local GigRecord the views read.
 * The rows carry no `_clocks` (viewers never merge ops) — an empty clock map is
 * correct: read-only render only touches `.fields`.
 */
export function scopedGigToRecord(sg: ScopedWireGig): GigRecord {
  const collections: GigRecord["state"]["collections"] = {};
  for (const [ckey, rows] of Object.entries(sg.collections ?? {})) {
    const byId: Record<string, { fields: Record<string, unknown>; _clocks: Record<string, string> }> = {};
    (rows ?? []).forEach((row, i) => {
      const itemId = String(row["item_id"] ?? row["id"] ?? `row-${i}`);
      byId[itemId] = { fields: row, _clocks: {} };
    });
    collections[ckey] = byId;
  }
  return {
    gig_id: sg.gig_id,
    rev: 0,
    state: {
      gig_id: sg.gig_id,
      gig: { fields: sg.fields ?? {}, _clocks: {} },
      collections,
      seen_op_ids: {},
      seen_dedup_keys: {},
    },
  };
}

/** Fetch the viewer's scoped gigs. Throws on a non-2xx (401 = bad/revoked key). */
export async function fetchViewerGigs(key: string, fetchImpl: typeof fetch = fetch): Promise<GigRecord[]> {
  const res = await fetchImpl("/api/viewer/gigs", {
    headers: { "X-Share-Key": key, Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`viewer ${res.status}`);
  const body = (await res.json()) as { gigs?: ScopedWireGig[] };
  return (body.gigs ?? []).map(scopedGigToRecord);
}
